/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavStoreProject} from 'constants/SourceConstants';
import {ProjectSettingsModal} from 'file-browser/ProjectSettingsModal';
import navigateToURL from 'lib/navigateToURL';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import Avatar, {getAvatarHeight} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import SettingsIcon from 'ui-library/icons/SettingsIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import PartyAvatarList from 'ui-library/PartyAvatarList';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';
import fuzzydate from 'util/fuzzydate';

const AVATAR_SIZE = AvatarSize.SMALL;

interface Props {
  onUpdateProjectSettings: (...args: Array<any>) => any;
  overrideInitialSort: boolean;
  projects: Array<NavStoreProject>;
  shouldShowOwnerColumn: boolean;
}

type State = any;

class ProjectsTable extends React.Component<Props, State> {
  static defaultProps = {overrideInitialSort: false};

  state: State = {
    /* A non-null sourceId will open the ProjectSettingsModal for that source. */
    sourceIdForProjectSettingsModal: null,
  };

  _handleCloseProjectSettingsModal = (): void => {
    // Close the modal.
    this.setState({sourceIdForProjectSettingsModal: null});
    // Refetch the projects to reflect any changes.
    this.props.onUpdateProjectSettings();
  };

  openProject = (project: any): void => {
    FullScreenActions.hideScreen();
    navigateToURL(project.url);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _getSortValue = (row: any, sortKey: any) => {
    const {project} = row.extra;
    if (sortKey === 's_owner') {
      return project[sortKey].name;
    }
    return project[sortKey];
  };

  _getRowContentHeight = (): number => getAvatarHeight(AVATAR_SIZE);

  _renderName = (project: any): JSX.Element => {
    const handleClick = (): void => {
      this.openProject(project);
    };
    const handleOpenProjectSettingsModal = (): void => {
      const {id: sourceId} = project;
      this.setState({sourceIdForProjectSettingsModal: sourceId});
    };
    return (
      <div>
        <a className="organization-projectLink" onClick={handleClick}>
          {project.name}
        </a>
        {project.permissions.admin && (
          <SettingsIcon
            className="u-mini-spacing-left tableRow--visibleOnHover-content"
            onClick={handleOpenProjectSettingsModal}
          />
        )}
      </div>
    );
  };

  render(): JSX.Element {
    const idsToProjects: {[id: string]: NavStoreProject} = this.props.projects.reduce(
      (mapping, currentProject) => {
        mapping[currentProject.id] = currentProject;
        return mapping;
      },
      {} as {[id: string]: NavStoreProject}
    );
    const columns = _.compact([
      {
        header: 'Name',
        accessorKey: 'name',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: true,
        sortKey: 'name',
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        isCollapsible: false,
        isSortable: true,
        sortKey: 'createdAt',
      },
      BenchlingConfig.ENABLE_PUBLIC_FOLDERS && {
        header: 'Visibility',
        accessorKey: 'public',
        isCollapsible: false,
        isSortable: true,
        sortKey: 'public',
      },
      this.props.shouldShowOwnerColumn && {
        header: 'Owner',
        accessorKey: 's_owner',
        isCollapsible: false,
        isSortable: true,
        sortKey: 's_owner',
      },
      {
        header: 'Collaborators',
        accessorKey: 'collaborators',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: false,
      },
    ]);

    const rows = this.props.projects.map((project) => ({
      id: project.id,
      data: {
        name: this._renderName(project),
        createdAt: fuzzydate(project.createdAt, {days: 31, showTime: false}),
        public: project.public ? 'Public' : 'Private',
        s_owner: <Avatar entity={project.s_owner} size={AVATAR_SIZE} showTooltip={true} />,
        collaborators: (
          <PartyAvatarList
            maxAvatars={5}
            parties={project.collaborators}
            size={AVATAR_SIZE}
            tooltipPlacement={FloatingPlacement.TOP}
          />
        ),
      },
      icon: <BenchlingIcon className="scrollableTable-cell-icon" icon={ModelTypeConstants.PROJECT} />,
      extra: {project},
    }));

    return (
      <div>
        <ModalAnchor open={Boolean(this.state.sourceIdForProjectSettingsModal)}>
          {this.state.sourceIdForProjectSettingsModal && (
            <ProjectSettingsModal
              permissions={idsToProjects[this.state.sourceIdForProjectSettingsModal].permissions}
              sourceId={this.state.sourceIdForProjectSettingsModal}
              onRequestClose={this._handleCloseProjectSettingsModal}
            />
          )}
        </ModalAnchor>
        <SortableScrollableTable
          columns={columns}
          getSortValue={this._getSortValue}
          hasLineBelowHeader={false}
          initialRows={rows}
          initialSort={this.props.overrideInitialSort ? [] : [{sortKey: 'name'}]}
          getRowContentHeight={this._getRowContentHeight}
        />
      </div>
    );
  }
}

export default ProjectsTable;
